import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import { v4 as uuidv4 } from 'uuid'
import { useQueryClient } from 'react-query'
import {
  useQuestionPutQuery,
  useQuestionPostQuery,
} from '../../Queries/QuestionQuery'
import 'react-quill/dist/quill.snow.css'
import { htmlToString, isQuizStarted } from '../../utils'
import { useStyles } from './AddMcqQuestionStyles'
import McqQuestionTile from './McqQuestionTile'
import EditMcqQuestionTile from './EditMcqQuestionTile'
import ConfirmBox from '../common/ConfirmBox'
import {
  useGetAllQuizzesForAQuestion,
  useUpdateQuestionForQuizzes,
} from '../../Queries/QuizQuery'
import CommonSnackbar from '../CommonSnackbar/CommonSnackbar'

function AddMcqQuestion({
  questionTypeFromParent,
  quizType,
  codingQuestionSwitch,
  handleQuestionTypeChange,
  questions,
  question,
  questionIndex,
  addQuestion,
  setAddQuestion,
  reload,
  setReload,
  quizId,
  quizStartTime,
  isPublished,
  save,
  setSave,
  setCancelAddQuestion,
  setErrorMessages,
  setTruthValue,
  refetchGetQueryById,
  postQuestionMapping,
  deleteMappingQuestion,
  refetchGetQuizQuestionQueryById,
  deleteQuestion,
  isWriteAllowed,
  setLocalQuestion,
  localQuestion,
  setTextEditorQuestion,
  textEditorQuestion,
  setEditViewQuestion,
  editViewQuestion,
  deleteQuestionId,
  setDeleteQuestionId,
  editOngoing,
  setEditOngoing,
  editQuestion,
  setEditQuestion,
}) {
  const queryClient = useQueryClient()
  const [errorMessage, setErrorMessage] = useState('')
  const [questionMcqType] = useState('mcq')
  const [backUpLocalQuestion, setBackUpLocalQuestion] = useState()
  const [openDialog, setOpenDialog] = useState(false)
  const [isMultipleAnswers, setIsMultipleAnswers] = useState(false)

  const [errors, setErrors] = useState({
    emptyQuestion: false,
    emptyMarks: false,
    marksNotValid: false,
    marksLessThanZero: false,
    emptyChoicesPresent: false,
    correctChoiceNotSelected: false,
    similarChoicesPresent: false,
    onlyOneChoicePresent: false,
  })

  const [affectedQuizes, setAffectedQuizes] = useState([])
  const [selectedQuizzes, setSelectedQuizzes] = useState([])
  const [showQuizesListModal, setShowQuizesListModal] = useState(false)

  const editQuestionQuery = useQuestionPutQuery(
    quizId,
    localQuestion?.id,
    questionMcqType,
    {
      retry: false,
      onSuccess: () => {
        setEditQuestion(NaN)
        setErrorMessages('Question Saved Successfully!')
        setTruthValue(true)
        setReload(reload + 1)
        refetchGetQueryById()
        refetchGetQuizQuestionQueryById()
      },
      onError: () => {
        setErrorMessages('Failed')
      },
    }
  )

  const postQuestion = useQuestionPostQuery({
    staleTime: 0,
    retry: false,

    onSuccess: (successResponse) => {
      const id = successResponse?.data?.data?.id
      const questionDetailsObj = {
        questionId: id,
        type: localQuestion?.type,
      }

      postQuestionMapping.mutate(questionDetailsObj)
      setTruthValue(true)
      setErrorMessages('Question Saved Successfully!')
      setAddQuestion(false)
      setReload(reload + 1)
      refetchGetQueryById()
      refetchGetQuizQuestionQueryById()
      queryClient.invalidateQueries('quizGetQueryById')
      setSave(false)
      setLocalQuestion({})
      setEditViewQuestion({})
      setTextEditorQuestion({ question: '<p></p>' })
    },

    onError: () => {
      setErrorMessages('Failed')
      setSave(false)
    },
  })

  const { refetch: refetchQuizzesForQuestion } = useGetAllQuizzesForAQuestion(
    localQuestion?.id,
    {
      enabled: false,
      onSuccess: (response) => {
        const filteredQuizzes = response?.filter((quiz) => {
          return quiz?.status === 'DRAFT' || quiz?.status === 'UPCOMING'
        })
        setAffectedQuizes(filteredQuizzes || [])
      },
    }
  )

  const updateQuestionsInQuizzes = useUpdateQuestionForQuizzes({
    onSuccess: async () => {
      setEditQuestion(NaN)
      setShowQuizesListModal(false)
      setErrorMessages('Question Saved Successfully!')
      setTruthValue(true)
      setAddQuestion(false)
      setReload(reload + 1)
      await refetchGetQueryById()
      await refetchGetQuizQuestionQueryById()
    },
  })

  const postNewQuestion = useQuestionPostQuery({
    staleTime: 0,
    retry: false,
    onSuccess: (successResponse) => {
      const id = successResponse?.data?.data?.id
      const newQuestionId = id
      updateQuestionsInQuizzes.mutate({
        oldQuestionId: localQuestion?.id,
        newQuestionId,
        type: localQuestion?.type,
        marks: localQuestion?.marks,
        quizIDs: selectedQuizzes.map((quiz) => quiz?.quizID),
      })
      queryClient.invalidateQueries('getAllQuizzesForAQuestion')
    },
  })

  useEffect(() => {
    if (localQuestion?.type === 'multi-choice') setIsMultipleAnswers(true)
    if (!Number.isNaN(editQuestion)) refetchQuizzesForQuestion()
  }, [editQuestion])

  useEffect(() => {
    if (question && !addQuestion) {
      if (question?.type === 'multi-choice') {
        setIsMultipleAnswers(true)
      }
      return
    }

    const isEditing =
      editViewQuestion?.id !== undefined &&
      editViewQuestion?.type === questionTypeFromParent

    const value = {
      id: isEditing ? editViewQuestion.id : `Added${uuidv4()}`,
      question: isEditing ? editViewQuestion.question : '<p></p>',
      choices: isEditing
        ? editViewQuestion.choices
        : [
            { id: `Added${uuidv4()}`, value: '', isCorrect: false },
            { id: `Added${uuidv4()}`, value: '', isCorrect: false },
          ],
      marks: isEditing ? editViewQuestion.marks : 2,
      type: isEditing ? editViewQuestion.type : questionTypeFromParent,
      isRandomize: isEditing ? editViewQuestion.isRandomize : false,
    }

    setLocalQuestion(value)
    setTextEditorQuestion((prev) => ({ ...prev, question: value.question }))
    setEditViewQuestion(value)
  }, [question, questionTypeFromParent])

  useEffect(() => {
    if (Number.isNaN(editQuestion)) {
      setBackUpLocalQuestion(localQuestion)
    }
  }, [localQuestion])

  const classes = useStyles()

  const marksValidation = (marksToBeValidated) => {
    if (!marksToBeValidated) {
      setErrors((prev) => ({
        ...prev,
        emptyMarks: true,
        marksLessThanZero: false,
        marksNotValid: false,
      }))
      return
    }
    if (!/^-?\d+\.?\d*$/.test(marksToBeValidated)) {
      setErrors((prev) => ({
        ...prev,
        emptyMarks: false,
        marksNotValid: true,
        marksLessThanZero: false,
      }))
      return
    }
    setErrors((prev) => ({
      ...prev,
      emptyMarks: false,
      marksNotValid: false,
      marksLessThanZero: Number(marksToBeValidated) <= 0,
    }))
  }

  const handleEdit = (event, setting, choiceIndex) => {
    const dummyQuestion = JSON.parse(JSON.stringify(localQuestion))
    if (setting === 'choice') {
      dummyQuestion.choices[choiceIndex].value = event.target.value
    }
    if (setting === 'marks') {
      marksValidation(event.target.value)
      dummyQuestion.marks = event.target.value
    }
    setLocalQuestion(dummyQuestion)
  }

  const removeId = (dummyQuestion) => {
    const modified = dummyQuestion?.choices?.map(({ id, ...rest }) => {
      let returnObj
      if (typeof id === 'string' && id.includes('Added')) {
        returnObj = { ...rest }
      } else {
        returnObj = { id, ...rest }
      }
      return returnObj
    })
    const temp = dummyQuestion
    temp.choices = modified
    return temp
  }

  const findSimilarElements = (choicesArray) => {
    const choiceCounts = {}

    choicesArray.forEach((element) => {
      choiceCounts[element] = (choiceCounts[element] || 0) + 1
    })

    const similarElements = Object.keys(choiceCounts || {}).filter(
      (element) => choiceCounts[element] > 1
    )
    return similarElements
  }

  const handleSave = async (value = '') => {
    const dummyLocalQuestion = JSON.parse(JSON.stringify(localQuestion))
    dummyLocalQuestion.question = textEditorQuestion.question

    const isOnlyOneChoicePresent =
      dummyLocalQuestion?.choices?.filter((choice) => {
        return choice?.value
      })?.length === 1
    const areEmptyChoicesPresent =
      dummyLocalQuestion?.choices?.filter((choice) => {
        return !choice?.value
      })?.length > 0
    const isCorrectChoicePresent =
      dummyLocalQuestion?.choices?.filter((choice) => {
        return !!choice?.isCorrect
      })?.length > 0

    const similarChoices = findSimilarElements(
      dummyLocalQuestion?.choices?.map((item) => item?.value)
    )

    if (htmlToString(dummyLocalQuestion?.question).trim() === '') {
      setErrors((errorsPrev) => {
        return { ...errorsPrev, emptyQuestion: true }
      })
      if (addQuestion) setSave(false)
      return
    }

    setErrors((errorsPrev) => {
      return { ...errorsPrev, emptyQuestion: false }
    })

    if (isOnlyOneChoicePresent) {
      setErrors((errorsPrev) => {
        setErrorMessage('Please add more choices')
        return { ...errorsPrev, onlyOneChoicePresent: true }
      })
      if (addQuestion) setSave(false)
      return
    }

    if (areEmptyChoicesPresent) {
      setErrors((errorsPrev) => {
        setErrorMessage('Empty choices are not allowed')
        return { ...errorsPrev, emptyChoicesPresent: true }
      })
      if (addQuestion) setSave(false)
      return
    }

    if (!isCorrectChoicePresent) {
      setErrors((errorsPrev) => {
        setErrorMessage('Select a correct choice')
        return { ...errorsPrev, correctChoiceNotSelected: true }
      })
      if (addQuestion) setSave(false)
      return
    }

    if (similarChoices?.length !== 0) {
      setErrors((errorsPrev) => {
        setErrorMessage('Similar Choices are present')
        return { ...errorsPrev, similarChoicesPresent: true }
      })
      if (addQuestion) setSave(false)
      return
    }

    dummyLocalQuestion.marks = parseInt(dummyLocalQuestion.marks, 10)
    dummyLocalQuestion.quizID = quizId

    if (value === 'duplicateEdit') {
      await postNewQuestion.mutateAsync(removeId(dummyLocalQuestion))
      setEditOngoing(false)
      return
    }

    if (!addQuestion) {
      if (dummyLocalQuestion.isEditable === false) {
        deleteMappingQuestion.mutate({
          quizId,
          questionId: dummyLocalQuestion?.id,
        })
        delete dummyLocalQuestion.id
        postQuestion.mutate(removeId(dummyLocalQuestion))
      } else {
        editQuestionQuery.mutate(removeId(dummyLocalQuestion))
      }
    }

    if (addQuestion) {
      delete dummyLocalQuestion.id
      postQuestion.mutate(removeId(dummyLocalQuestion))
    }

    setEditOngoing(false)
  }

  useEffect(() => {
    if (save && addQuestion) handleSave()
  }, [save, addQuestion])

  const handleClick = () => {
    deleteQuestion.mutate({
      quizId,
      questionId: deleteQuestionId,
      questionType:
        questionMcqType === 'multi-choice' ? 'mcq' : questionMcqType,
    })
    setOpenDialog(false)
    setDeleteQuestionId(undefined)
  }
  const handleClose = () => {
    setOpenDialog(false)
  }

  const handleDelete = () => {
    setDeleteQuestionId(question?.id)
    setOpenDialog(true)
  }

  const handleChoice = (setting) => {
    const dummyQuestion = JSON.parse(JSON.stringify(localQuestion))
    const index = dummyQuestion?.choices?.length
    if (setting === 'Add')
      dummyQuestion.choices
        .splice(index + 1, 0, {
          id: `Added${uuidv4()}`,
          value: '',
          isCorrect: false,
        })
        .join()

    setLocalQuestion(dummyQuestion)
  }

  const handleCorrectChoice = (selectedChoiceId) => {
    const dummyLocalQuestion = JSON.parse(JSON.stringify(localQuestion))
    const modifiedChoices = dummyLocalQuestion.choices.map((choice) => {
      const copyChoice = JSON.parse(JSON.stringify(choice))
      copyChoice.isCorrect = choice?.id === selectedChoiceId
      return copyChoice
    })
    dummyLocalQuestion.choices = modifiedChoices
    setLocalQuestion(dummyLocalQuestion)
  }

  const handleMultiChoice = (selectedChoiceId) => {
    const dummyLocalQuestion = JSON.parse(JSON.stringify(localQuestion))
    const modifiedChoices = dummyLocalQuestion.choices.map((choice) => {
      if (choice?.id === selectedChoiceId)
        return { ...choice, isCorrect: !choice.isCorrect }
      return choice
    })
    dummyLocalQuestion.choices = modifiedChoices
    setLocalQuestion(dummyLocalQuestion)
  }

  const handleRemoveChoice = (choiceIndex) => {
    const dummyQuestion = JSON.parse(JSON.stringify(localQuestion))
    const filterChoices = dummyQuestion?.choices?.filter(
      (x) => x.id !== choiceIndex
    )
    dummyQuestion.choices = filterChoices
    setLocalQuestion(dummyQuestion)
  }
  const handleCancel = () => {
    setEditOngoing(false)
    setEditQuestion(NaN)
    setLocalQuestion(backUpLocalQuestion)
    setAddQuestion(false)
    setTextEditorQuestion((prevQuestion) => {
      const updatePrevQuestion = { ...prevQuestion }
      updatePrevQuestion.value = backUpLocalQuestion?.question
      return updatePrevQuestion
    })
    if (isMultipleAnswers && localQuestion?.type === 'mcq') {
      setIsMultipleAnswers(false)
    }
  }
  const handleCancelAddMcq = () => {
    setEditOngoing(false)
    setAddQuestion(false)
    setCancelAddQuestion(true)
    setEditViewQuestion({})
    setLocalQuestion({})
  }

  const handleChange = () => {
    if (editOngoing) {
      setErrorMessage('Please finish the previous edit')
    } else {
      setLocalQuestion(question)
      setTextEditorQuestion({ question: question?.question })
      setEditQuestion(questionIndex)
      setEditOngoing(true)
    }
  }

  const updateQuestionInSelectedQuizzes = async () => {
    const dummyLocalQuestion = JSON.parse(JSON.stringify(localQuestion))
    dummyLocalQuestion.question = textEditorQuestion.question
    delete dummyLocalQuestion.id
    dummyLocalQuestion.quizId = quizId
    await postNewQuestion.mutateAsync(removeId(dummyLocalQuestion))
  }

  return (
    <Grid container className={classes.root}>
      <CommonSnackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={errorMessage !== ''}
        autoHideDuration={1000}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={() => setErrorMessage('')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        key={errorMessage}
        severity="error"
      />
      {editQuestion === questionIndex || addQuestion ? (
        <EditMcqQuestionTile
          question={question}
          addQuestion={addQuestion}
          questions={questions}
          questionIndex={questionIndex}
          localQuestion={localQuestion}
          setLocalQuestion={setLocalQuestion}
          errors={errors}
          questionTypeFromParent={questionTypeFromParent}
          isQuizStarted={isQuizStarted}
          quizStartTime={quizStartTime}
          handleEdit={handleEdit}
          isPublished={isPublished}
          handleQuestionTypeChange={handleQuestionTypeChange}
          quizType={quizType}
          codingQuestionSwitch={codingQuestionSwitch}
          textEditorQuestion={textEditorQuestion}
          setTextEditorQuestion={setTextEditorQuestion}
          handleCorrectChoice={handleCorrectChoice}
          handleRemoveChoice={handleRemoveChoice}
          handleChoice={handleChoice}
          editQuestionQuery={editQuestionQuery}
          postQuestion={postQuestion}
          editQuestion={editQuestion}
          handleCancel={handleCancel}
          handleCancelAddMcq={handleCancelAddMcq}
          handleSave={handleSave}
          handleMultiChoice={handleMultiChoice}
          showQuizesListModal={showQuizesListModal}
          setShowQuizesListModal={setShowQuizesListModal}
          affectedQuizes={affectedQuizes}
          updateQuestionInSelectedQuizzes={updateQuestionInSelectedQuizzes}
          setSelectedQuizzes={setSelectedQuizzes}
          selectedQuizzes={selectedQuizzes}
          quizId={quizId}
          setEditViewQuestion={setEditViewQuestion}
          editViewQuestion={editViewQuestion}
        />
      ) : (
        <McqQuestionTile
          questionIndex={questionIndex}
          editQuestion={editQuestion}
          addQuestion={addQuestion}
          question={question}
          handleChange={handleChange}
          isPublished={isPublished}
          isQuizStarted={isQuizStarted}
          quizStartTime={quizStartTime}
          deleteQuestion={deleteQuestion}
          handleDelete={handleDelete}
          isWriteAllowed={isWriteAllowed}
        />
      )}
      <ConfirmBox
        isOpenDialog={openDialog}
        handleClose={handleClose}
        handleClick={handleClick}
        boxTitle="Delete Question"
        boxDescription="Are you sure you want to delete this question?"
        styles={classes}
        buttonName="Delete"
      />
    </Grid>
  )
}

export default AddMcqQuestion
